import classNames from 'classnames';
import numbro from 'numbro';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { generalHelpers } from '../../helpers';

const KNOB_WIDTH = 0.1;

const Styles = styled.div`
  height: 30px;
  width: 100%;
  position: relative;

  .control-container {
    height: 100%;
    width: 100%;
    position: relative;
    user-select: none;
    div,
    span {
      line-height: 12px !important;
    }

    .slider-bar {
      position: absolute;
      left: 0;
      top: 15%;
      width: 100%;
      height: 10%;
      background: #dddddd;
      border-radius: 2px;
    }

    .slider-bar-filled {
      position: absolute;
      top: 15%;
      height: 10%;
      background: #128dfa;
      border-radius: 5px;
    }

    .knob {
      position: absolute;
      top: 0%;
      width: ${KNOB_WIDTH * 100}%;
      height: 40%;
      background: white;
      cursor: pointer;
      border: solid 1px #aaa;
      border-radius: 4px;
      box-shadow: 0px 1px 1px #ddd;
    }

    .knob:hover {
      background: #eee;
    }

    .label {
      position: absolute;
      top: 45%;
      font-size: 8px;
      font-weight: bold;
      border: solid 1px #aaa;
      border-radius: 4px;
      padding: 1px 2px;
    }

    .label-left {
      left: 0;
    }

    .label-right {
      right: 0;
    }
  }

  &.is-popup {
    height: 50px;
  }

  .popup-slider-container {
    position: fixed;
    bottom: -100vh;
    height: calc(120px + 20px + 50px + 10px); // 10px is added to avoid gap
    left: 0;
    right: 0;
    padding: 20px;
    padding-bottom: 120px;
    background-color: #fffffff5;
    border-top: solid #ccc;
    z-index: 2147483001; // max z-index, conver everything else.
    transition: bottom 0.5s ease;

    &.visible {
      bottom: 0;
    }
  }

  & > .control-container {
    transform: translateY(0);
    transition: transform 0.5s ease;
  }
  &.has-popup > .control-container {
    transform: translateY(150%);
  }
`;
interface Props {
  minValue: number;
  maxValue: number;
  leftValue: number;
  rightValue: number;
  percentage?: boolean;
  onChange: (leftValue: number, rightValue: number) => void;
  isPopup?: boolean;
}

const normalizeEvent = e => {
  // if it is a touch event, clientX is hidden in targetTouches
  if (
    e.type.startsWith('touch') &&
    e.targetTouches &&
    e.targetTouches.length > 0
  ) {
    e.preventDefault();
    e.stopPropagation();
    return e.targetTouches[0];
  }

  return e;
};

const RangeSlider: React.FC<Props> = ({
  minValue,
  maxValue,
  leftValue,
  rightValue,
  percentage,
  onChange,
  isPopup,
}) => {
  const ref = useRef();

  const leftXPa = useCallback(
    (left: number) =>
      ((left - minValue) / (maxValue - minValue)) * 0.8 + 0.1 - KNOB_WIDTH,
    [maxValue, minValue]
  );

  const rightXPa = useCallback(
    (right: number) => ((right - minValue) / (maxValue - minValue)) * 0.8 + 0.1,
    [maxValue, minValue]
  );

  const [t] = useTranslation();
  const [showPopup, setShowPopup] = useState(false);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    if (ref.current) {
      const slideBar: HTMLElement = ref.current;
      const filledBar = slideBar.querySelector('.slider-bar-filled');
      const knobLeft = slideBar.querySelector('.knob-left');
      const knobRight = slideBar.querySelector('.knob-right');
      const labelLeft = slideBar.querySelector('.label-left');
      const labelRight = slideBar.querySelector('.label-right');

      let left = leftValue,
        right = rightValue;

      const resetUI = () => {
        labelLeft.textContent = `${numbro(
          percentage ? left * 100 : left
        ).format({ totalLength: 3 })}${percentage ? '%' : ''}`;
        labelRight.textContent = `${numbro(
          percentage ? right * 100 : right
        ).format({ totalLength: 3 })}${percentage ? '%' : ''}`;
        knobLeft['style'].left = `${leftXPa(left) * 100}%`;
        knobRight['style'].left = `${rightXPa(right) * 100}%`;
        filledBar['style'].left = `${leftXPa(left) * 100}%`;
        filledBar['style'].width = `${
          (rightXPa(right) - leftXPa(left) + 0.01) * 100
        }%`;
      };

      resetUI();

      let initMouseX: number;
      let slideBarWidth: number;
      let currentKnob: string = null; // null, "l" or "r" represents the current active knob
      let initValue: number;

      const handleMouseDown = e => {
        e = normalizeEvent(e);

        initMouseX = e.clientX;
        initValue = currentKnob === 'l' ? left : right;
        slideBarWidth = slideBar.clientWidth * 0.8;
      };

      const handleMouseUp = () => {
        if (currentKnob) {
          onChange(left, right);
        }
        currentKnob = null;
      };

      const handleMouseMove = e => {
        if (currentKnob === null) {
          return;
        }

        e = normalizeEvent(e);

        const deltaX = e.clientX - initMouseX;
        const deltaPa = deltaX / slideBarWidth;
        const deltaValue = deltaPa * (maxValue - minValue);
        const newValue = Math.min(
          maxValue,
          Math.max(minValue, initValue + deltaValue)
        );

        if (currentKnob === 'l') {
          left = Math.min(right, newValue);
        } else {
          right = Math.max(left, newValue);
        }

        resetUI();
      };

      const handleMouseDownLeft = e => {
        currentKnob = 'l';
        handleMouseDown(e);
      };

      const handleMouseDownRight = e => {
        currentKnob = 'r';
        handleMouseDown(e);
      };

      const showPopup = () => setShowPopup(true);

      slideBar.addEventListener('touchend', showPopup);
      knobLeft.addEventListener('mousedown', handleMouseDownLeft);
      knobRight.addEventListener('mousedown', handleMouseDownRight);
      knobLeft.addEventListener('touchstart', handleMouseDownLeft);
      knobRight.addEventListener('touchstart', handleMouseDownRight);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('touchmove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchend', handleMouseUp);
      document.addEventListener('touchcancel', handleMouseUp);

      return () => {
        slideBar && slideBar.removeEventListener('touchend', showPopup);
        knobLeft &&
          knobLeft.removeEventListener('mousedown', handleMouseDownLeft);
        knobRight &&
          knobRight.removeEventListener('mousedown', handleMouseDownRight);
        knobLeft &&
          knobLeft.removeEventListener('touchstart', handleMouseDownLeft);
        knobRight &&
          knobRight.removeEventListener('touchstart', handleMouseDownRight);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchend', handleMouseUp);
        document.removeEventListener('touchcancel', handleMouseUp);
      };
    } else {
      return generalHelpers.noop;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    leftXPa,
    minValue,
    maxValue,
    rightXPa,
    percentage,
    onChange,
    ref.current,
  ]);

  return (
    <Styles
      ref={ref}
      className={classNames({
        'is-popup': isPopup,
        'has-popup': !isPopup && showPopup,
      })}
    >
      <div className="control-container">
        <div className="slider-bar" />
        <div className="slider-bar-filled" />
        <div className="knob knob-left" />
        <div className="knob knob-right" />
        <span className="label label-left" />
        <span className="label label-right" />
      </div>
      {!isPopup && (
        <div
          className={classNames('popup-slider-container', {
            visible: showPopup,
          })}
        >
          <RangeSlider
            minValue={minValue}
            maxValue={maxValue}
            leftValue={leftValue}
            rightValue={rightValue}
            percentage={percentage}
            onChange={onChange}
            isPopup
          />
          <Button
            variant="outline-secondary"
            onClick={() => setShowPopup(false)}
          >
            {t('close-popup')}
          </Button>
        </div>
      )}
    </Styles>
  );
};

export default RangeSlider;
